import { useState, useEffect, useRef } from 'react';
import maplibregl from 'maplibre-gl';
import 'maplibre-gl/dist/maplibre-gl.css';
import Supercluster from 'supercluster';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Search, Loader2, Layers } from 'lucide-react';
import { toast } from '@/hooks/use-toast';
import { useLocationSearch } from '@/hooks/useLocationSearch';
import { fetchBusinessesInBounds } from '@/utils/homepageApi';
import type { Feature, FeatureCollection, Point } from 'geojson';

// Add custom styles
import './TestMapPage.css';

interface Business {
  _id: string;
  name: string;
  description: string;
  emoji: string;
  location: {
    coordinates: {
      coordinates: [number, number];
    };
  };
  hasAnimalActivities?: boolean;
  hasAdventureActivities?: boolean;
}

interface MapLayers {
  markers: boolean;
}

type DisplayMode = 'heatmap' | 'clusters' | 'individuals';

// Define types for our cluster properties
interface BusinessProperties {
  id: string;
  name: string;
  description: string;
  emoji: string;
}

type ClusterFeature = Feature<Point> & {
  properties: BusinessProperties & {
    cluster?: boolean;
    cluster_id?: number;
    point_count?: number;
    point_count_abbreviated?: string;
  };
};

export function TestMapPage() {
  const mapContainer = useRef<HTMLDivElement>(null);
  const map = useRef<maplibregl.Map | null>(null);
  const markers = useRef<{ [key: string]: maplibregl.Marker }>({});
  const supercluster = useRef<Supercluster>();
  const [searchQuery, setSearchQuery] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const [businesses, setBusinesses] = useState<Business[]>([]);
  const [isLoadingBusinesses, setIsLoadingBusinesses] = useState(false);
  const [layers, setLayers] = useState<MapLayers>({
    markers: true
  });
  const [currentDisplayMode, setCurrentDisplayMode] = useState<DisplayMode>('heatmap');
  const [hasSearched, setHasSearched] = useState(false);
  const { searchLocation } = useLocationSearch();
  const zoomTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Helper function to determine zoom level based on location type
  const getZoomLevelForLocationType = (locationType: string): number => {
    switch (locationType) {
      case 'country':
        return 5; 
      case 'state':
      case 'province':
      case 'region':
        return 7;
      case 'county':
        return 8;
      case 'city':
      case 'town':
        return 11;
      case 'village':
      case 'hamlet':
      case 'suburb':
        return 13;
      case 'attraction':
      case 'natural':
      case 'building':
        return 15;
      default:
        return 9;
    }
  };

  // Function to create custom marker element
  const createMarkerElement = (emoji: string, count?: number) => {
    const markerElement = document.createElement('div');
    markerElement.className = 'custom-marker';
    markerElement.style.width = count ? '50px' : '44px';
    markerElement.style.height = count ? '50px' : '44px';
    markerElement.style.backgroundColor = count ? '#2C5F2D' : '#ffffff';
    markerElement.style.borderRadius = '50%';
    markerElement.style.border = `3px solid ${count ? '#ffffff' : '#2C5F2D'}`;
    markerElement.style.display = 'flex';
    markerElement.style.alignItems = 'center';
    markerElement.style.justifyContent = 'center';
    markerElement.style.fontSize = count ? '16px' : '24px';
    markerElement.style.color = count ? '#ffffff' : '#000000';
    markerElement.style.cursor = 'pointer';
    markerElement.style.boxShadow = '0 2px 4px rgba(0,0,0,0.2)';
    markerElement.innerHTML = count ? `${count}` : (emoji || '📍');
    return markerElement;
  };

  // Update markers with clustering
  const updateMarkers = (businesses: Business[], mapInstance: maplibregl.Map) => {
    // Remove old markers
    Object.values(markers.current).forEach(marker => marker.remove());
    markers.current = {};

    if (!layers.markers || !mapInstance) return;

    // Create GeoJSON features with non-null properties
    const features: Array<Feature<Point, BusinessProperties>> = businesses.map(business => ({
      type: 'Feature',
      geometry: {
        type: 'Point',
        coordinates: business.location.coordinates.coordinates
      },
      properties: {
        id: business._id,
        name: business.name,
        description: business.description,
        emoji: business.emoji || '📍'
      }
    }));

    // Initialize Supercluster with proper typing
    supercluster.current = new Supercluster({
      radius: 40,
      maxZoom: 16,
      minPoints: 2
    });

    // Load features into the cluster
    supercluster.current.load(features);

    // Get current bounds and zoom
    const bounds = mapInstance.getBounds();
    const zoom = Math.floor(mapInstance.getZoom());

    // Get clusters and points
    const clusters = supercluster.current.getClusters(
      [bounds.getWest(), bounds.getSouth(), bounds.getEast(), bounds.getNorth()],
      zoom
    ) as ClusterFeature[];

    // Create markers for clusters and individual points
    clusters.forEach(cluster => {
      const [lng, lat] = cluster.geometry.coordinates;
      const properties = cluster.properties;

      if (properties.cluster) {
        // Create cluster marker
        const count = properties.point_count || 0;
        const markerElement = createMarkerElement('', count);

        const marker = new maplibregl.Marker({
          element: markerElement,
          anchor: 'center'
        })
          .setLngLat([lng, lat])
          .addTo(mapInstance);

        // Add click handler for clusters
        markerElement.addEventListener('click', () => {
          if (!supercluster.current || !properties.cluster_id) return;
          
          const expansionZoom = Math.min(
            supercluster.current.getClusterExpansionZoom(properties.cluster_id),
            20
          );
          
          mapInstance.flyTo({
            center: [lng, lat],
            zoom: expansionZoom,
            speed: 1.5
          });
        });

        markers.current[`cluster-${properties.cluster_id}`] = marker;
      } else {
        // Create individual marker
        const markerElement = createMarkerElement(properties.emoji);

        const marker = new maplibregl.Marker({
          element: markerElement,
          anchor: 'center'
        })
          .setLngLat([lng, lat])
          .addTo(mapInstance);

        // Add click handler for individual markers
        markerElement.addEventListener('click', () => {
          toast({
            title: properties.name,
            description: properties.description.slice(0, 100) + (properties.description.length > 100 ? '...' : ''),
          });
        });

        markers.current[properties.id] = marker;
      }
    });
  };

  // Calculate display mode based on business density and zoom level
  const calculateDisplayMode = (businesses: Business[], mapInstance: maplibregl.Map): DisplayMode => {
    if (!mapInstance || businesses.length === 0) return 'heatmap';

    const currentZoom = mapInstance.getZoom();
    
    // Group businesses by proximity
    const groups: Business[][] = [];
    const processed = new Set<string>();

    businesses.forEach(business => {
      if (processed.has(business._id)) return;

      const group: Business[] = [business];
      processed.add(business._id);

      businesses.forEach(otherBusiness => {
        if (processed.has(otherBusiness._id)) return;

        const [lng1, lat1] = business.location.coordinates.coordinates;
        const [lng2, lat2] = otherBusiness.location.coordinates.coordinates;
        
        // Calculate distance using Haversine formula
        const R = 6371; // Earth's radius in km
        const dLat = (lat2 - lat1) * Math.PI / 180;
        const dLon = (lng2 - lng1) * Math.PI / 180;
        const a = 
          Math.sin(dLat/2) * Math.sin(dLat/2) +
          Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) * 
          Math.sin(dLon/2) * Math.sin(dLon/2);
        const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
        const distance = R * c;

        // If businesses are within 2km of each other, group them
        if (distance < 2) {
          group.push(otherBusiness);
          processed.add(otherBusiness._id);
        }
      });

      groups.push(group);
    });

    // Determine display mode based on groups
    const hasLargeGroups = groups.some(group => group.length >= 3);
    const hasSmallGroups = groups.some(group => group.length === 2);
    const hasIndividualMarkers = groups.some(group => group.length === 1);

    if (currentZoom < 8 || hasLargeGroups) {
      return 'heatmap';
    } else if (currentZoom < 12 || hasSmallGroups) {
      return 'clusters';
    } else {
      return 'individuals';
    }
  };

  // Update display mode when map moves or businesses change
  const updateDisplayMode = (mapInstance: maplibregl.Map) => {
    if (!mapInstance || !layers.markers) return;
    
    const newMode = calculateDisplayMode(businesses, mapInstance);
    if (newMode !== currentDisplayMode) {
      setCurrentDisplayMode(newMode);
    }
  };

  // Initialize map
  useEffect(() => {
    if (!mapContainer.current || map.current || !hasSearched) return;

    console.log('Initializing map...');
    try {
      const mapInstance = new maplibregl.Map({
        container: mapContainer.current,
        style: {
          version: 8,
          sources: {
            'osm': {
              type: 'raster',
              tiles: ['https://tile.openstreetmap.org/{z}/{x}/{y}.png'],
              tileSize: 256,
              attribution: '© OpenStreetMap contributors'
            }
          },
          layers: [
            {
              id: 'osm',
              type: 'raster',
              source: 'osm',
              minzoom: 0,
              maxzoom: 19
            }
          ]
        },
        center: [-3.7038, 40.4168], // Madrid
        zoom: 5
      });

      console.log('Map instance created');

      // Handle map movement to load businesses
      mapInstance.on('moveend', async () => {
        if (!mapInstance) return;
        
        const bounds = mapInstance.getBounds();
        const mapBounds = {
          northEast: {
            latitude: bounds.getNorthEast().lat,
            longitude: bounds.getNorthEast().lng
          },
          southWest: {
            latitude: bounds.getSouthWest().lat,
            longitude: bounds.getSouthWest().lng
          }
        };

        try {
          setIsLoadingBusinesses(true);
          const data = await fetchBusinessesInBounds(mapBounds);
          console.log('Fetched businesses:', data);
          
          if (!data || data.length === 0) {
            console.log('No businesses found in bounds');
            return;
          }

          setBusinesses(data);
          updateMarkers(data, mapInstance);
          
        } catch (error) {
          console.error('Error loading businesses:', error);
          toast({
            title: 'Error',
            description: 'Failed to load businesses in this area',
            variant: 'destructive'
          });
        } finally {
          setIsLoadingBusinesses(false);
        }
      });

      // Add navigation controls
      mapInstance.addControl(new maplibregl.NavigationControl(), 'top-right');
      mapInstance.addControl(new maplibregl.ScaleControl(), 'bottom-left');
      mapInstance.addControl(new maplibregl.FullscreenControl());

      map.current = mapInstance;

      // Trigger initial data load
      mapInstance.fire('moveend');

      return () => {
        Object.values(markers.current).forEach(marker => marker.remove());
        mapInstance.remove();
        map.current = null;
      };
    } catch (error) {
      console.error('Error initializing map:', error);
      toast({
        title: 'Error',
        description: 'Failed to initialize the map',
        variant: 'destructive'
      });
    }
  }, [hasSearched, layers.markers]);

  // Update markers when layers.markers changes
  useEffect(() => {
    if (!map.current) return;
    updateMarkers(businesses, map.current);
  }, [layers.markers, businesses]);

  // Update layer visibility based on display mode
  useEffect(() => {
    if (!map.current) return;

    const updateLayerVisibility = () => {
      if (!map.current) return;

      // Update heatmap visibility
      if (map.current.getLayer('business-heat')) {
        map.current.setLayoutProperty(
          'business-heat',
          'visibility',
          (layers.markers && currentDisplayMode === 'heatmap') ? 'visible' : 'none'
        );
      }

      // Update cluster layers visibility
      const clusterLayers = ['clusters', 'cluster-count'];
      clusterLayers.forEach(layerId => {
        if (map.current?.getLayer(layerId)) {
          map.current.setLayoutProperty(
            layerId,
            'visibility',
            (layers.markers && currentDisplayMode === 'clusters') ? 'visible' : 'none'
          );
        }
      });

      // Update individual markers
      if (layers.markers) {
        updateMarkers(businesses, map.current);
      } else {
        Object.values(markers.current).forEach(marker => marker.remove());
        markers.current = {};
      }
    };

    updateLayerVisibility();
  }, [currentDisplayMode, layers.markers, businesses]);

  // Update display mode on zoom
  useEffect(() => {
    if (!map.current) return;

    const handleZoom = () => {
      if (zoomTimeoutRef.current) {
        clearTimeout(zoomTimeoutRef.current);
      }
      zoomTimeoutRef.current = setTimeout(() => {
        updateDisplayMode(map.current!);
      }, 100);
    };

    map.current.on('zoom', handleZoom);
    return () => {
      if (zoomTimeoutRef.current) {
        clearTimeout(zoomTimeoutRef.current);
      }
      map.current?.off('zoom', handleZoom);
    };
  }, [businesses]);

  // Add map move handler to update clusters
  useEffect(() => {
    const mapInstance = map.current;
    if (!mapInstance) return;

    const moveHandler = () => {
      if (businesses.length > 0) {
        updateMarkers(businesses, mapInstance);
      }
    };

    mapInstance.on('moveend', moveHandler);
    mapInstance.on('zoomend', moveHandler);

    return () => {
      mapInstance.off('moveend', moveHandler);
      mapInstance.off('zoomend', moveHandler);
    };
  }, [businesses, layers.markers]);

  // Handle location search
  const handleSearch = async () => {
    if (!searchQuery.trim()) return;

    setIsSearching(true);
    try {
      const result = await searchLocation(searchQuery);
      if (!result) {
        toast({
          title: 'Location not found',
          description: 'Please try a different search term',
          variant: 'destructive'
        });
        return;
      }

      // Set hasSearched to true to initialize map
      setHasSearched(true);

      // Get appropriate zoom level based on location type
      const zoomLevel = getZoomLevelForLocationType(result.locationType);

      // Wait for map to be initialized
      if (!map.current) {
        await new Promise(resolve => setTimeout(resolve, 100));
      }

      // Fly to location with smooth animation
      map.current?.flyTo({
        center: result.mapLibreCoordinates,
        zoom: zoomLevel,
        duration: 2000,
        essential: true
      });

      toast({
        title: 'Location found',
        description: `Moving to ${result.displayName} (${result.locationType})`,
      });
    } catch (error) {
      console.error('Search error:', error);
      toast({
        title: 'Search failed',
        description: 'An error occurred while searching for the location',
        variant: 'destructive'
      });
    } finally {
      setIsSearching(false);
    }
  };

  return (
    <div className="h-screen flex flex-col">
      {/* Header with search and layer controls */}
      <div className="p-4 bg-white shadow-sm">
        <div className="max-w-3xl mx-auto flex gap-2">
          <Input
            type="text"
            placeholder="Search for a location..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
            className="flex-1"
          />
          <Button 
            onClick={handleSearch}
            disabled={isSearching}
          >
            {isSearching ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Search className="h-4 w-4" />
            )}
            <span className="ml-2">Search</span>
          </Button>
        </div>

        {/* Layer controls */}
        <div className="max-w-3xl mx-auto mt-2 flex gap-2">
          <Button
            variant={layers.markers ? "default" : "outline"}
            size="sm"
            onClick={() => setLayers(prev => ({ ...prev, markers: !prev.markers }))}
          >
            Markers
          </Button>
        </div>
      </div>

      {/* Map container */}
      <div className="flex-1 relative min-h-[500px]">
        {hasSearched ? (
          <div 
            ref={mapContainer} 
            className="absolute inset-0"
            style={{ 
              width: '100%',
              height: '100%'
            }}
          />
        ) : (
          <div className="absolute inset-0 flex items-center justify-center bg-gray-50">
            <div className="text-center">
              <Search className="h-12 w-12 mx-auto text-gray-400 mb-4" />
              <h3 className="text-lg font-medium text-gray-900">Search for a location to view the map</h3>
              <p className="mt-1 text-sm text-gray-500">Enter a city, region, or landmark to get started</p>
            </div>
          </div>
        )}
        
        {/* Loading indicator */}
        {isLoadingBusinesses && (
          <div className="absolute top-4 left-1/2 transform -translate-x-1/2 loading-indicator">
            <div className="flex items-center gap-2">
              <Loader2 className="h-4 w-4 animate-spin text-green-600" />
              <span className="text-sm font-medium text-gray-700">Loading businesses...</span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
} 